<template>
  <div class="animated fadeIn">
    <div class="row">

      <div class="col">
        <b-card>
          <template slot="header">
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="structureTreeselect"
                              v-model="surveyValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.SurveyFilter.Placeholder')"
                              :options="structureItems"
                              :disabled="isExporting"
                              :disableFuzzyMatching="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :disable-branch-nodes="true"
                              @input="loadData">
                  </treeselect>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="waveTreeselect"
                              v-model="waveValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.WaveFilter.Placeholder')"
                              :options="waveItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              @close="loadData"
                              @input="filterInput">
                  </treeselect>
              </div>
              <div class="form-group col-md">
                  <treeselect ref="scenarioTreeselect"
                              v-model="scenarioValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.ScenarioFilter.Placeholder')"
                              :options="scenarioItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              @close="loadData"
                              @input="filterInput">
                  </treeselect>
              </div>
              <div class="form-group col-md">
                <treeselect ref="propertyTreeselect"
                            v-model="propertyValue"
                            :auto-load-root-options="false"
                            :placeholder="$t('Webportal.PropertyValueFilter.Placeholder')"
                            :options="propertyItems"
                            :disabled="noSurveySelected || isExporting"
                            :disableFuzzyMatching="true"
                            :multiple="true"
                            :showCount="true"
                            showCountOf="LEAF_DESCENDANTS"
                            :flat="true"
                            :defaultExpandLevel="1"
                            :disable-branch-nodes="true"
                            @close="loadData"
                            @input="filterInput">

                </treeselect>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md">
                  <treeselect ref="uploadTreeselect"
                              v-model="uploadValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.UploadFilter.Placeholder')"
                              :options="uploadItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              @close="loadData"
                              @input="filterInput">
                  </treeselect>
              </div>
              <div class="form-group col-md-auto">
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadUploadFiles')">
                  <b-button variant="primary"
                            :disabled="isExporting || downloadDisabled"
                            @click="downloadUploadFiles()">
                    {{ $t('Webportal.Download') }}
                    <font-awesome-icon icon="download" />
                  </b-button>
                </span>
              </div>
              <div class="form-group col-md">
                <div class="input-group d-flex justify-content-end">
                  <b-pagination v-model="selectedPage"
                                :total-rows="totalCount"
                                :per-page="pageSize"
                                :limit="5"
                                @change="getSurveyInstances"
                                class="mb-0"></b-pagination>
                    <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ShowAllChecks')">
                      <b-button variant="primary"
                                :disabled="getAllSurveyInstancesDisabled"
                                @click="getAllSurveyInstances">
                        {{ $n(this.totalCount) }}
                      </b-button>
                    </span>
                </div>
              </div>
            </div>
          </template>

          <div v-bind:class="{ spinner: isLoading || isExporting }" style="min-height:300px;">
            <!-- Important: use @click.stop and @click.native.stop for action items inside table
                https://bootstrap-vue.js.org/docs/components/table/ -->
            <b-table v-if="!this.noSurveySelected"
                    small hover responsive :fixed="browserIsIE"
                    :foot-clone="items && items.length >= 5"
                    :items="items"
                    :fields="fields"
                    no-local-sorting
                    @sort-changed="sortChanged"
                    @row-clicked="rowClicked">
              <template v-slot:head(Select)>
                <b-form-checkbox v-model="toggleSelectionChecked"
                                :indeterminate="toggleSelectionIndeterminate"
                                style="min-height:1.0rem"
                                @click.native.stop
                                @change="toggleSelection"
                                v-b-tooltip.hover.nofade="toolTipDelay"
                                :title="$t('Webportal.SelectAll')" />
              </template>
              <template v-slot:head(Survey.Title)>{{ $t('Webportal.Survey') }}</template>
              <template v-slot:head(SurveyInstance.ExternalSurveyInstanceId)>{{ $t('Webportal.SurveyInstanceId') }}</template>
              <template v-slot:head(ClientLocation.ClientLocationIdOnClientSite)>{{ $t('Webportal.LocationId') }}</template>
              <template v-slot:head(ClientLocation)>{{ $t('Webportal.LocationAddress') }}</template>
              <template v-slot:head(SurveyInstance.TimeStampSurveyStart)>{{ $t('Webportal.CalendarWeek') }}</template>
              <template v-slot:head(SurveyInstance.Result)>{{ $t('Webportal.Result') }}</template>
              <template v-slot:head(SurveyInstance.SurveyInstanceStatusId)>{{ $t('Webportal.Status') }}</template>

              <template slot="top-row">
                <td></td>
                <td></td>
                <td>
                  <search-input small v-model.trim="columnFilter.SurveyInstance.ExternalSurveyInstanceId" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.ClientLocationIdOnClientSite" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.FullAddress" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-if="isCalendarWeekFieldVisible">
                  <search-input small v-model.trim="columnFilter.SurveyInstance.CalendarWeek" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.SurveyInstance.Result" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-if="isMsmUser" colspan="2">
                  <search-select small v-model="columnFilter.SurveyInstance.SurveyInstanceStatusId"
                                :options="surveyInstanceStatusOptions" @change="loadData"
                                :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                </td>
                <td v-else></td>
              </template>

              <template v-slot:cell(Select)="data">
                <b-form-checkbox v-model="data.item.Selected"
                                @click.native.stop
                                @change="updateToggleSelection" />
              </template>
              <template v-slot:cell(Survey.Title)="data">
                {{ data.value }}
              </template>
              <template v-slot:cell(SurveyInstance.ExternalSurveyInstanceId)="data">
                {{ data.value }}
              </template>
              <template v-slot:cell(ClientLocation.ClientLocationIdOnClientSite)="data">
                {{ data.value }}
              </template>
              <template v-slot:cell(ClientLocation)="data">
                {{ data.value.Name }}<br>
                {{ data.value.Address }}<br>
                {{ data.value.City }}, {{ data.value.PostalCode }}
              </template>
              <template v-slot:cell(SurveyInstance.TimeStampSurveyStart)="data">
                {{ calendarWeekAndDateFormatted(data.item) }}
              </template>
              <template v-slot:cell(SurveyInstance.Result)="data">
                {{ (data.value != null) ? $n(data.value) + '%' : '' }}
              </template>
              <template v-slot:cell(SurveyInstance.SurveyInstanceStatusId)="data">
                <font-awesome-icon icon="cogs"
                  v-if="data.item.SurveyInstance.SurveyInstanceStatusId <= 215"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="$t('Webportal.StatusProcessing')" />
                <font-awesome-icon icon="trash-alt"
                  v-else-if="data.item.SurveyInstance.SurveyInstanceStatusId === 300"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="$t('Webportal.StatusDeleted')" />
                <font-awesome-icon icon="check"
                  v-else
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="$t('Webportal.StatusOK')" />
              </template>
              <template v-slot:cell(Action)="data">
                <b-button v-if="data.item.HasVideoUpload"
                          pill variant="primary" size="sm" @click="showPreview(data.item.SurveyInstance.SurveyInstanceId)"
                          v-b-tooltip.hover.nofade="toolTipDelay"
                          :title="$t('Webportal.ShowVideo')"
                          @click.stop>
                  <font-awesome-icon icon="video" />
                </b-button>
              </template>
            </b-table>
          </div>
        </b-card>
      </div>

      <div class="col-5 p-0" v-bind:class="{ 'd-none': !isPreviewVisible }">
        <div class="pdf-viewer card col-5 p-0">
          <div>
            <button type="button" class="close" aria-label="Close" @click="closePreview()"
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.ClosePreview')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="card-body pt-0" v-bind:class="{ spinner: isPreviewLoading }">
            <video id="video" class="w-100" ref="videoControl" controls>
              <source :src="previewUrl" type="video/mp4" />
              <p>OOPS! It looks like your browser doesn't support HTML5 videos. Please use the latest version of a modern browser.</p>
            </video>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppConfig from '@/mixins/AppConfig'
import DownloadFileMixin from '@/mixins/DownloadFileMixin'

import SearchInput from '@/components/SearchInput'
import SearchSelect from '@/components/SearchSelect'

import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService'
import MediaService from '@/services/MediaService'
import AuthenticationService from '@/services/AuthenticationService'
import AdministationService from '@/services/AdministationService';

const PAGE_SIZE_DEFAULT = 20;
const PAGE_SIZE_MAXIMUM = 999999;

export default {
  name: "MediaExplorer",

  components: { SearchInput, SearchSelect },

  mixins: [ AppConfig, DownloadFileMixin ],

  data () {
    return {
      isLoading: false,                     // Determines if data loading is currently in progress

      // Filter properties
      clientSettings: null,
      structureItems: [],                   // Data items for structure treeselect
      surveyValue: null,                    // Selected structure items
      waveItems: [],                        // Data items for wave treeselect
      waveValue: [],                        // Selected wave items
      scenarioItems: [],                    // Data items for scenarios
      scenarioValue: [],                    // Selected scenarios
      propertyItems: [],                    // Data items for property treeselect
      propertyValue: [],                    // Selected property items
      uploadItems: [],
      uploadValue: [],

      columnFilter: {                       // Filter values for each column
        Survey: { Title: null },
        SurveyInstance : {
          ExternalSurveyInstanceId: null,
          CalendarWeek: null,
          Result: null,
          SurveyInstanceStatusId: null },
        ClientLocation: {
          ClientLocationIdOnClientSite: null,
          FullAddress: null }
      },

      sortBy: null,                         // Sorting column
      sortDescending: false,                // Sorting order

      currentSurveyValue: null,             // Current selected structure item for change detection
      currentFilter: null,                  // Current filter object for change detection

      isExporting: false,

      // Paging properties
      pageSize: PAGE_SIZE_DEFAULT,          // Number of items on one page of the table
      selectedPage: 1,                      // Number of the page, that is currently visible
      totalCount: 0,                        // Number of total items

      // Table properties
      items: [],
      fields: [
        { key: 'Select', thStyle: 'width:2em' },
        { key: 'Survey.Title', thStyle: '' },
        { key: 'SurveyInstance.ExternalSurveyInstanceId', thStyle: 'width:7em', sortable: true },
        { key: 'ClientLocation.ClientLocationIdOnClientSite', thStyle: 'width:8em', sortable: true },
        { key: 'ClientLocation', thStyle: '', sortable: true },
        { key: 'SurveyInstance.TimeStampSurveyStart', class: 'text-center', thStyle: 'width:11em', sortable: true },
        { key: 'SurveyInstance.Result', class: 'text-right', thStyle: 'width:6em', sortable: true },
        { key: 'SurveyInstance.SurveyInstanceStatusId', thStyle: 'width:5em', thClass: 'text-center', tdClass: 'text-center', sortable: true },
        { key: 'Action', label: '', thStyle: 'width:3em' }
      ],
      isCalendarWeekFieldVisible: true,

      toggleSelectionChecked: false,
      toggleSelectionIndeterminate: false,

      previewUrl: null,
      isPreviewLoading: false,
      isPreviewVisible: false,
      previewCancelToken: null,
      surveyInstancesCancelToken: null,

      toolTipDelay: { delay: { "show": 500, "hide": 100 } }
    }
  },

  computed: {
    filter: function() {
      // Creates a new object without empty values from source
      const isNotEmptyPredicate = o => !_.isEmpty(o);
      const pickByDeep = (obj, fn) =>
        _.pickBy( // Remove on root level
          _.mapValues(obj, (val, key) =>
            !_.isObjectLike(val) ? val : // Take plain values
              !_.isEmpty(_.pickBy(val, fn)) ? _.pickBy(val, fn) : null) // Map values of nested object
          , fn)

      let result = {
        clientIds: [],
        projectIds: [],
        surveyIds: [],
        waveIds: this.waveValue,
        scenarioIds: this.scenarioValue,
        propertyIds: this.propertyValue,
        searchText: this.searchText,
        columnFilter: pickByDeep(this.columnFilter, isNotEmptyPredicate),
        sortBy: this.sortBy,
        sortDescending: this.sortDescending
      };

      if (this.surveyValue)
      {
        let id = this.surveyValue.substring(1);
        result.surveyIds.push(id);
      }

      return result;
    },

    options: function() {
      let result = {
        page: this.selectedPage,
        pageSize: this.pageSize,
        withUploads: true
      }

      return result;
    },

    noSurveySelected: function () {
      return _.isEmpty(this.surveyValue);
    },

    getAllSurveyInstancesDisabled: function () {
      return this.totalCount <= PAGE_SIZE_DEFAULT || this.totalCount >= 1000 || this.pageSize === PAGE_SIZE_MAXIMUM;
    },

    selectedSurveyInstanceIds: function () {
      return _.map(this.items.filter(item => item.Selected), 'SurveyInstance.SurveyInstanceId');
    },

    downloadDisabled: function () {
      return _.isEmpty(this.items) || _.isEmpty(this.uploadValue) ||_.isEmpty(this.selectedSurveyInstanceIds);
    },

    isMsmUser: function() {
      return AuthenticationService.isMSMUser;
    },

    surveyInstanceStatusOptions: function() {
      return [
          { value: '220', text: this.$t('Webportal.StatusOK') },
          { value: '300', text: this.$t('Webportal.StatusDeleted') },
          { value: '<=215', text: this.$t('Webportal.StatusProcessing') }
      ]
    },
  },

  mounted() {
    // Remove status column for non MSM users
    if (!this.isMsmUser)
      _.remove(this.fields, item => { return item.key === 'SurveyInstance.SurveyInstanceStatusId' });

    this.getClientSettings();
    this.getSurveyStructure();
    this.getSurveyWaves();
    this.getSurveyUploads();
    this.loadData();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyStructure();
      this.getSurveyWaves();
      this.getSurveyUploads();
      this.getSurveyInstances();
    },

    getClientSettings() {
      AdministationService.getClientSettings()
        .then(response => {
          this.clientSettings = response.data ? new Map(response.data.map(item => [item.ClientId, item])) : null;

          // Show calendar week column only if one of the clients the user has permission for is configured to show check date or time
          this.isCalendarWeekFieldVisible = this.isMsmUser || response.data && response.data.some(cs => cs.ShowCheckDate || cs.ShowCheckTime);
          if (!this.isCalendarWeekFieldVisible)
            this.fields.find(f => f.key == 'SurveyInstance.TimeStampSurveyStart').class += " d-none";
        });
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;
        })
    },

    // Get survey structure data for the structure treeselect
    getSurveyWaves() {
      if (!this.noSurveySelected) {
        SurveysService.getSurveyWaves(this.filter)
          .then(response => {
            this.waveItems = response.data;
          })
      }
    },

    // Get survey structure data for the structure treeselect
    getSurveyProperties() {
      if (!this.noSurveySelected) {
        SurveysService.getSurveyCustomProperties(this.filter, true)
          .then(response => {
            this.propertyItems = response.data;
          })
      };
    },

    // Get survey structure data for the scenario treeselect
    getSurveyScenarios() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyScenarios(this.filter)
          .then(response => {
            this.scenarioItems =  response.data;
          });
      }
    },

    // Get survey structure data for the structure treeselect
    getSurveyUploads() {
      if (!this.noSurveySelected) {
        SurveysService.getSurveyUploads(this.filter)
          .then(response => {
            this.uploadItems = response.data;
          })
      }
    },

    // Gets survey instances data for the table from the server, including current filter and pagination
    getSurveyInstances(page) {
      if (page) this.selectedPage = page;
      this.toggleSelectionChecked = false;
      this.toggleSelectionIndeterminate = false;

      if (!this.noSurveySelected) {
        this.isLoading = true;

        // Cancel previous request
        if (this.surveyInstancesCancelToken) {
          this.surveyInstancesCancelToken.cancel();
        }

        this.surveyInstancesCancelToken = axios.CancelToken.source();

        SurveysService.getSurveyInstances(this.filter, this.options, this.surveyInstancesCancelToken)
          .then(response => {
            this.totalCount = response.data.TotalCount;
            this.items = response.data.items;
            this.isLoading = false;
          })
          .catch((thrown) => {
            // TODO
          });
      }
      else {
        this.totalCount = 0;
        this.items = [];
      }
    },

    getAllSurveyInstances() {
      this.selectedPage = 1;
      this.pageSize = PAGE_SIZE_MAXIMUM;
      this.getSurveyInstances();
    },

    // Applies changed filter and triggeres data loading if necessary
    loadData() {
      // Only load data when effective filter has changed
      // TODO: The order of array elements shouldn't matter when comparing
      if (!_.isEqual(this.filter, this.currentFilter)) {
        let surveyValueChanged = !_.isEqual(this.currentSurveyValue, this.surveyValue);
        this.currentSurveyValue = this.surveyValue;
        if (surveyValueChanged)
        {
          this.waveValue = [];
          this.propertyValue = [];
          this.uploadValue = [];
          this.scenarioValue = [];
        }
        this.currentFilter = this.filter;

        this.selectedPage = 1;
        this.pageSize = PAGE_SIZE_DEFAULT;

        this.getSurveyInstances();
        if (surveyValueChanged)
        {
          this.getSurveyWaves();
          this.getSurveyProperties();
          this.getSurveyUploads();
          this.getSurveyScenarios();
        }
      }
    },

    // Called whenever the input of a treeselect control changes
    filterInput(value) {
      // Workaround: Only call data loading when clear all is pressed
      if (_.size(value) === 0)
        this.loadData();
    },

    sortChanged(e) {
      this.sortBy = e.sortBy;
      this.sortDescending = e.sortDesc;
      this.loadData();
    },

    // Selects a row when clicked
    rowClicked(item) {
      let checked = item.Selected ? !item.Selected : true;

      this.updateToggleSelection(checked);

      // Use $set to register the new property for vue tracking
      this.$set(item, 'Selected', checked);
    },

    toggleSelection(checked) {
      _.forEach(this.items, item => {
          this.$set(item, 'Selected', checked);
        });
    },

    updateToggleSelection(checked) {
      let count = 0;

      _.forEach(this.items, item => {
          if (item.Selected) count++;
        });

      // New value is not written to property right now
      checked ? count++ : count--;

      // Handle changes in individual flavour checkboxes
      if (count === 0) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = false;
      } else if (count === this.items.length) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = true;
      } else {
        this.toggleSelectionIndeterminate = true;
        this.toggleSelectionChecked = false;
      }
    },

    downloadUploadFiles() {
      this.isExporting = true;
      SurveysService.getSurveyUploadFiles(this.selectedSurveyInstanceIds, this.uploadValue)
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
        })
        .catch(_ => { this.isExporting = false; } );
    },

    showPreview(id) {
      this.$refs.videoControl.pause();

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('showPreview');

      this.previewCancelToken = axios.CancelToken.source();

      this.isPreviewLoading = true;
      this.isPreviewVisible = true;

      MediaService.getVideoToken(id, this.previewCancelToken)
        .then((response) => {
          this.isPreviewLoading = false;

          this.previewUrl =`${process.env.BASE_URL}api/media/stream?token=${response.data.token}`;
          this.$refs.videoControl.load();
          this.$refs.videoControl.play();
        })
        .catch((thrown) => {
          if (thrown.message != 'showPreview')
          {
            this.previewUrl = null;
            this.isPreviewLoading = false;
          }

          if (axios.isCancel(thrown)) {
          }
        });
    },

    closePreview() {
      this.$refs.videoControl.pause();
      this.isPreviewLoading = false;
      this.isPreviewVisible = false;
      this.previewUrl = null;

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('closePreview');
    },

    calendarWeekAndDateFormatted(item) {
      let cs = this.clientSettings ? this.clientSettings.get(item.Client.ClientId) : null;

      if (this.isMsmUser || (cs && cs.ShowCheckDate && cs.ShowCheckTime))
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_datetime(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (cs && cs.ShowCheckDate)
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_date(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (cs && cs.ShowCheckTime)
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_time(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else
        return item.SurveyInstance.CalendarWeek
    }
  }
};
</script>